import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { api } from "./lib/api";
import { safeLocalStorageGet, safeLocalStorageSet } from "./lib/storage";
import { Sidebar } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";
import { AnnouncementBanner } from "./components/AnnouncementBanner";
import { CommandPalette } from "./components/CommandPalette";
import { Home } from "./pages/Home";
import { Practice } from "./pages/Practice";
import { Round } from "./pages/Round";
import { Profile } from "./pages/Profile";
import { Equipment } from "./pages/Equipment";
import { RequireAuth } from "./components/RequireAuth";
import { Login } from "./pages/auth/Login";
import { Signup } from "./pages/auth/Signup";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { VerifyEmail } from "./pages/auth/VerifyEmail";
import { AcceptInvite } from "./pages/auth/AcceptInvite";
import { Terms } from "./pages/auth/Terms";
import { AuthProvider, useAuth } from "./lib/auth";
import { SCORECARD_IMPORT_FEATURE } from "./lib/features";
import { RequireFeature } from "./components/RequireFeature";
import { ArrowLogProvider } from "./lib/arrowLog";
import { Loading } from "./components/Loading";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";

// Heavy routes — code-split so they only load when navigated to.
// Every lazy boundary below sits inside a <ChunkErrorBoundary>: after a
// deploy an old tab's dynamic import of a deleted hashed chunk rejects, and
// the boundary reloads the page once (sessionStorage-guarded) instead of
// dropping the whole app onto the root crash sentence.
const Score       = lazy(() => import("./pages/Score").then(m => ({ default: m.Score })));
const NewRound    = lazy(() => import("./pages/NewRound").then(m => ({ default: m.NewRound })));
const RoundDetail = lazy(() => import("./pages/RoundDetail").then(m => ({ default: m.RoundDetail })));
const History     = lazy(() => import("./pages/History").then(m => ({ default: m.History })));
const Stats       = lazy(() => import("./pages/Stats").then(m => ({ default: m.Stats })));
const Settings    = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const JoinBale      = lazy(() => import("./pages/JoinBale").then(m => ({ default: m.JoinBale })));
const WizardInvite  = lazy(() => import("./pages/WizardInvite"));
const Help          = lazy(() => import("./pages/Help"));
const ImportReview  = lazy(() => import("./pages/ImportReview").then(m => ({ default: m.ImportReview })));
const Counter       = lazy(() => import("./pages/Counter").then(m => ({ default: m.Counter })));
// ── M13-W2 anchor scaffold — each agent replaces ONLY its own placeholder line(s); leave the others as-is ──
// ── M13-W2 [#2321] Format editor lazy imports ──
const Formats      = lazy(() => import("./pages/Formats").then(m => ({ default: m.Formats })));
const FormatEditor = lazy(() => import("./pages/FormatEditor").then(m => ({ default: m.FormatEditor })));
// ── M13-W2 [#2322] Upload scorecard lazy import ──
const UploadScorecard = lazy(() => import("./pages/Upload").then(m => ({ default: m.Upload })));
// ── M13-W2 end ──

// RBTE — hidden Between Ends mirror (no Sidebar entry; CommandPalette + URL only)
const RbteSearch     = lazy(() => import("./pages/rbte/RbteSearch").then(m => ({ default: m.RbteSearch })));
const RbteTournament = lazy(() => import("./pages/rbte/RbteTournament").then(m => ({ default: m.RbteTournament })));
const RbteStats      = lazy(() => import("./pages/rbte/RbteStats").then(m => ({ default: m.RbteStats })));

const STORAGE_KEY = "xring.theme";

const CRUMBS = {
  "/new-round": ["Score", "New round"],
  "/count":     ["Score", "Count arrows"],
  "/practice":  ["Practice", "Sessions"],
  "/round":     ["Rounds", "Most recent"],
  "/history":   ["Rounds", "History"],
  "/stats":     ["Stats", "Analytics"],
};

export function makeCrumbs(pathname) {
  if (CRUMBS[pathname]) return CRUMBS[pathname];
  if (/^\/rounds\/\d+$/.test(pathname)) return ["Rounds", "Detail"];
  if (/^\/imports\//.test(pathname)) return ["Score", "Import review"];
  if (pathname === "/rbte") return ["RBTE", "Tournaments"];
  if (/^\/rbte\/t\/\d+$/.test(pathname)) return ["RBTE", "Tournament"];
  if (pathname === "/rbte/stats") return ["RBTE", "Stats"];
  return null;
}

const KIND_LABEL = {
  league:     "Leagues",
  tournament: "Tournaments",
  practice:   "Practice",
};

function scoreCrumbs(session) {
  return [KIND_LABEL[session?.kind] || "Scoring"];
}

function Shell() {
  const { activeArcher } = useAuth();
  const [activeSession, setActiveSession] = useState(null);
  const [paletteOpen, setPaletteOpen]     = useState(false);
  const [navOpen, setNavOpen]             = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return safeLocalStorageGet(STORAGE_KEY) || "light";
  });
  const location = useLocation();

  // sync theme to DOM + localStorage
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    safeLocalStorageSet(STORAGE_KEY, theme);
  }, [theme]);

  // ⌘K / Ctrl+K global shortcut
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
      if (e.key === "Escape") setPaletteOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Reset scroll and close the mobile nav drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
    window.scrollTo(0, 0);
    api.recordPageview(location.pathname).catch(() => {});
  }, [location.pathname]);

  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));
  const crumbs = location.pathname === "/score"
    ? scoreCrumbs(activeSession)
    : makeCrumbs(location.pathname);

  return (
    <div className="app">
      <Sidebar
        activeSession={activeSession}
        mobileOpen={navOpen}
        onClose={() => setNavOpen(false)}
      />
      <Topbar
        crumbs={crumbs}
        onToggleTheme={toggleTheme}

        onOpenPalette={() => setPaletteOpen(true)}
        onOpenNav={() => setNavOpen(true)}
      />
      <AnnouncementBanner />
      {/* Keying the routes by the active archer remounts every page on a
          workspace switch, so each one refetches its now-scoped data. */}
      <ChunkErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<Loading />}>
        <Routes key={activeArcher?.id ?? "none"}>
          <Route path="/"        element={<Home />} />
          <Route path="/score"   element={<Score onActiveSessionChange={setActiveSession} />} />
          <Route path="/new-round" element={<NewRound />} />
          <Route path="/count"   element={<Counter />} />
          <Route path="/practice" element={<Practice />} />
          <Route path="/round"         element={<Round />} />
          <Route path="/rounds/:id"    element={<RoundDetail />} />
          <Route path="/imports/:jobId" element={<ImportReview />} />
          {/* ── M13-W2 [#2321] Formats routes ── */}
          <Route path="/formats"     element={<Formats />} />
          <Route path="/formats/new" element={<FormatEditor />} />
          <Route path="/formats/:id" element={<FormatEditor />} />
          {/* ── M13-W2 [#2322] Upload route ── */}
          <Route
            path="/upload"
            element={
              <RequireFeature feature={SCORECARD_IMPORT_FEATURE}>
                <UploadScorecard />
              </RequireFeature>
            }
          />
          {/* ── M13-W2 end ── */}
          <Route path="/history"       element={<History />} />
          <Route path="/stats"   element={<Stats />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/equipment" element={<Equipment />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/help"    element={<Help />} />
          <Route path="/rbte" element={<RbteSearch />} />
          <Route path="/rbte/t/:beId" element={<RbteTournament />} />
          <Route path="/rbte/stats" element={<RbteStats />} />
          <Route path="*"        element={<Home />} />
        </Routes>
      </Suspense>
      </ChunkErrorBoundary>

      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          onToggleTheme={toggleTheme}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public, shell-less auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/terms" element={<Terms />} />

          {/* Shell-less scoring routes */}
          <Route
            path="/bale/join/:token"
            element={
              <ChunkErrorBoundary>
                <Suspense fallback={<div className="auth-shell"><div className="auth-loading">Loading…</div></div>}>
                  <JoinBale />
                </Suspense>
              </ChunkErrorBoundary>
            }
          />
          <Route
            path="/wizard-invite/:token"
            element={
              <ChunkErrorBoundary>
                <Suspense fallback={<div className="auth-shell"><div className="auth-loading">Loading…</div></div>}>
                  <WizardInvite />
                </Suspense>
              </ChunkErrorBoundary>
            }
          />

          {/* Everything else lives behind the auth guard, inside the shell */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                {/* Global "Log arrows" sheet (#1943) — one app-level instance
                    shared by the Sidebar nav item, Home's quick action, and
                    History's kebab-edit. */}
                <ArrowLogProvider>
                  <Shell />
                </ArrowLogProvider>
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
